import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faRocket,
  faBuilding,
  faUsers,
  faGlobe,
  faArrowRight,
  faStar,
  faChartLine,
  faRobot,
  faShieldAlt,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  const colors = {
    navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
    gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
    white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF'
  };

  const handleSignup = (plan: string) => {
    navigate(`/agent-portal/signup?plan=${plan}`);
  };

  return (
    <div style={{ background: colors.navy, color: colors.white, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: '100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Navigation */}
      <nav style={{ background: `linear-gradient(135deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderBottom: `2px solid ${colors.gold}`, padding: '16px 0' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px' }}>
              AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '18px' }}>.com</span>
            </span>
          </a>
          <div className="d-flex gap-3 align-items-center">
            <a href="/" style={{ color: colors.muted, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>Home</a>
            <a href="/pricing" style={{ color: colors.gold, textDecoration: 'none', fontSize: '14px', fontWeight: 700 }}>Pricing</a>
            <Button
              variant="outline-light"
              size="sm"
              style={{ borderColor: colors.muted, color: colors.muted, fontSize: '13px' }}
              onClick={() => navigate('/agent-portal/login')}
            >
              Agent Login
            </Button>
            <Button
              size="sm"
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, fontSize: '13px' }}
              onClick={() => navigate('/agent-portal/signup')}
            >
              Get Started
            </Button>
          </div>
        </Container>
      </nav>

      {/* Hero */}
      <section style={{ padding: '50px 0 30px', textAlign: 'center' }}>
        <Container>
          <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', color: colors.gold, marginBottom: '12px' }}>
            Simple, Transparent Pricing
          </p>
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '52px', letterSpacing: '2px', marginBottom: '16px', color: colors.white }}>
            Choose Your Plan
          </h1>
          <p style={{ fontSize: '18px', color: colors.muted, maxWidth: '600px', margin: '0 auto 12px', lineHeight: 1.6 }}>
            AI-powered buyer search and seller CMA tools — all under your brand. No contract. Cancel anytime.
          </p>
          <div className="d-flex justify-content-center gap-3 mt-3">
            <span style={{ background: 'rgba(26,107,60,0.2)', color: '#60c090', fontSize: '12px', fontWeight: 700, padding: '5px 14px', borderRadius: '4px' }}>
              1.6M+ Transactions Trained
            </span>
            <span style={{ background: 'rgba(201,149,42,0.15)', color: colors.gold2, fontSize: '12px', fontWeight: 700, padding: '5px 14px', borderRadius: '4px' }}>
              197 Zip-Code AI Models
            </span>
            <span style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', fontSize: '12px', fontWeight: 700, padding: '5px 14px', borderRadius: '4px' }}>
              90% Prediction Accuracy
            </span>
          </div>
        </Container>
      </section>

      {/* Pricing Cards */}
      <section style={{ padding: '30px 0 50px' }}>
        <Container>
          <Row className="g-4 justify-content-center">
            {/* CRM Starter */}
            <Col lg={3}>
              <div style={{ background: colors.navy2, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(201,149,42,0.15)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: colors.navy3, padding: '24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <FontAwesomeIcon icon={faRocket} size="2x" style={{ color: colors.gold2, marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.muted, marginBottom: '8px' }}>Agent CRM</div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, lineHeight: 1 }}>
                    $70<span style={{ fontSize: '16px', color: colors.muted }}>/mo</span>
                  </div>
                  <p style={{ fontSize: '13px', color: colors.muted, marginTop: '8px', marginBottom: 0 }}>CRM + MLS search tools</p>
                </div>
                <div style={{ padding: '24px', flex: 1 }}>
                  {[
                    'Client Hub — manage all leads',
                    'Buyer & seller profiles',
                    'MLS natural language search',
                    'Property results with photos',
                    'AI home valuation with comps',
                    'Email clients from your Gmail/Outlook',
                    'Notes & activity tracking',
                    'Your branding on all pages',
                    'Fair Housing compliant',
                    'MLS included — your broker authorizes, we connect'
                  ].map((f, i) => (
                    <div key={i} style={{ fontSize: '14px', color: colors.off, padding: '5px 0', display: 'flex', gap: '10px', alignItems: 'baseline', lineHeight: 1.4 }}>
                      <span style={{ color: colors.gold2, fontWeight: 700 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <div style={{ padding: '24px', paddingTop: 0 }}>
                  <Button
                    size="lg"
                    className="w-100 fw-bold"
                    style={{ background: colors.gold, border: 'none', color: colors.navy, fontSize: '15px', padding: '14px' }}
                    onClick={() => handleSignup('agent-hub')}
                  >
                    Get Started <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </Button>
                  <p style={{ fontSize: '11px', color: colors.muted, textAlign: 'center', marginTop: '8px', marginBottom: 0 }}>
                    Upgrade anytime to unlock AI buyer search portal
                  </p>
                </div>
              </div>
            </Col>

            {/* AI Search - Most Popular */}
            <Col lg={3}>
              <div style={{ background: colors.navy2, borderRadius: '12px', overflow: 'hidden', border: `2px solid ${colors.gold}`, boxShadow: '0 0 30px rgba(201,149,42,0.2)', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '-1px', left: '50%', transform: 'translateX(-50%)', background: colors.gold, color: colors.navy, fontSize: '10px', fontWeight: 700, letterSpacing: '1px', padding: '4px 16px', borderRadius: '0 0 6px 6px' }}>
                  <FontAwesomeIcon icon={faStar} className="me-1" /> MOST POPULAR
                </div>
                <div style={{ background: colors.gold, padding: '24px', textAlign: 'center' }}>
                  <FontAwesomeIcon icon={faUsers} size="2x" style={{ color: colors.navy, marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.navy, marginBottom: '8px' }}>AI Search</div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.navy, lineHeight: 1 }}>
                    $135<span style={{ fontSize: '16px', color: colors.navy2 }}>/mo</span>
                  </div>
                  <p style={{ fontSize: '13px', color: colors.navy2, marginTop: '8px', marginBottom: 0 }}>AI-powered buyer & seller tools</p>
                </div>
                <div style={{ padding: '24px', flex: 1 }}>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: colors.gold, marginBottom: '12px' }}>Everything in Agent CRM, plus:</p>
                  {[
                    'AI buyer search portal — your clients get their own link',
                    'Buyers chat with AI 24/7 in natural language',
                    'AI-powered property alerts matched to buyer criteria',
                    'Smart CMA reports with AI market analysis',
                    'Shareable seller reports with AI valuation chat',
                    'Comparable sales with condition classification',
                    'Real-time email sync — inbound & outbound history',
                    'Send before listing appointments to win the listing'
                  ].map((f, i) => (
                    <div key={i} style={{ fontSize: '14px', color: colors.off, padding: '5px 0', display: 'flex', gap: '10px', alignItems: 'baseline', lineHeight: 1.4 }}>
                      <span style={{ color: colors.gold2, fontWeight: 700 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <div style={{ padding: '24px', paddingTop: 0 }}>
                  <Button
                    size="lg"
                    className="w-100 fw-bold"
                    style={{ background: colors.white, border: 'none', color: colors.navy, fontSize: '15px', padding: '14px' }}
                    onClick={() => handleSignup('agent')}
                  >
                    Get Started <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </Button>
                </div>
              </div>
            </Col>

            {/* AI Pro */}
            <Col lg={3}>
              <div style={{ background: colors.navy2, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(201,149,42,0.15)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: colors.navy3, padding: '24px', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <FontAwesomeIcon icon={faGlobe} size="2x" style={{ color: colors.gold2, marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.muted, marginBottom: '8px' }}>AI Pro</div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, lineHeight: 1 }}>
                    $185<span style={{ fontSize: '16px', color: colors.muted }}>/mo</span>
                  </div>
                  <p style={{ fontSize: '13px', color: colors.muted, marginTop: '8px', marginBottom: 0 }}>Full automation suite</p>
                </div>
                <div style={{ padding: '24px', flex: 1 }}>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: colors.gold, marginBottom: '12px' }}>Everything in AI Search, plus:</p>
                  {[
                    'AI Lead Coach — analyzes leads, recommends next action',
                    'Instant AI response to new leads — even at 2 AM',
                    '30-day automated action plans per lead',
                    'AI-drafted follow-ups — you review, edit & send',
                    'Monthly seller CMA auto-updates with market data',
                    'AI evaluation of new listing alerts for buyers',
                    'Smart follow-up sequences (Day 1, 3, 7)',
                    'AI daily lead digest'
                                    ].map((f, i) => (
                    <div key={i} style={{ fontSize: '14px', color: colors.off, padding: '5px 0', display: 'flex', gap: '10px', alignItems: 'baseline', lineHeight: 1.4 }}>
                      <span style={{ color: colors.gold2, fontWeight: 700 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <div style={{ padding: '24px', paddingTop: 0 }}>
                  <Button
                    size="lg"
                    className="w-100 fw-bold"
                    style={{ background: colors.gold, border: 'none', color: colors.navy, fontSize: '15px', padding: '14px' }}
                    onClick={() => handleSignup('agent')}
                  >
                    Get Started <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </Button>
                </div>
              </div>
            </Col>

            {/* Broker Portal */}
            <Col lg={3}>
              <div style={{ background: 'linear-gradient(135deg, #0a1628, #162844)', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(201,149,42,0.2)', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: 'rgba(201,149,42,0.08)', padding: '24px', textAlign: 'center', borderBottom: '1px solid rgba(201,149,42,0.15)' }}>
                  <FontAwesomeIcon icon={faBuilding} size="2x" style={{ color: colors.gold2, marginBottom: '12px' }} />
                  <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: colors.gold, marginBottom: '8px' }}>Broker Portal</div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, lineHeight: 1 }}>
                    $500<span style={{ fontSize: '16px', color: colors.muted }}>/mo</span>
                  </div>
                  <p style={{ fontSize: '13px', color: colors.muted, marginTop: '8px', marginBottom: 0 }}>Includes 2 agent seats</p>
                  <p style={{ fontSize: '11px', color: colors.gold2, marginTop: '4px', marginBottom: 0 }}>Unlimited free compliance agents + $70/mo per Agent Hub upgrade</p>
                  <span style={{ display: 'inline-block', background: 'rgba(201,149,42,0.15)', color: colors.gold2, fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '3px', marginTop: '8px', letterSpacing: '1px' }}>COMING MAY 2026</span>
                </div>
                <div style={{ padding: '24px', flex: 1 }}>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: colors.gold, marginBottom: '12px' }}>Full brokerage management:</p>
                  {[
                    '2 Agent Hub seats included ($140 value)',
                    'Unlimited free compliance-only agents',
                    'Agents upgrade to AI Search or AI Pro individually',
                    'Transaction document management',
                    'AI document review (real-time feedback)',
                    'Commission management & payouts',
                    'Broker compliance dashboard',
                    'Ask the Broker AI chatbot',
                    'Agent onboarding & oversight',
                    'Priority enterprise support'
                  ].map((f, i) => (
                    <div key={i} style={{ fontSize: '14px', color: colors.off, padding: '5px 0', display: 'flex', gap: '10px', alignItems: 'baseline', lineHeight: 1.4 }}>
                      <span style={{ color: colors.gold2, fontWeight: 700 }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <div style={{ padding: '24px', paddingTop: 0 }}>
                  <Button
                    size="lg"
                    className="w-100 fw-bold"
                    style={{ background: 'transparent', border: `2px solid ${colors.gold}`, color: colors.gold, fontSize: '15px', padding: '14px' }}
                    onClick={() => window.location.href = 'mailto:support@kairosiq.ai?subject=Broker Portal Interest'}
                  >
                    Contact Sales
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Sponsor Partner CTA */}
          <div style={{ background: 'linear-gradient(135deg, #0a1628, #162844)', borderRadius: '12px', padding: '32px', marginTop: '32px', border: `1px solid rgba(201,149,42,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <FontAwesomeIcon icon={faUsers} style={{ color: colors.gold2, fontSize: 20 }} />
                <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: colors.white }}>Sponsor Partner Program</span>
              </div>
              <p style={{ fontSize: 14, color: colors.muted, margin: 0, maxWidth: 600 }}>
                Lenders, title companies, and coaches — equip your agent network with AI tools at scale.
                Purchase seats in bulk. Your agents get full AI Pro access. You get business intelligence.
              </p>
            </div>
            <Button
              size="lg"
              style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', color: colors.navy, fontWeight: 700, fontSize: 14, padding: '12px 28px', whiteSpace: 'nowrap' }}
              onClick={() => navigate('/sponsor/signup')}
            >
              Learn More <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: 8 }} />
            </Button>
          </div>

          {/* Website Add-On */}
          <div style={{ background: colors.navy3, borderRadius: '12px', padding: '32px', marginTop: '32px', border: `1px solid rgba(201,149,42,0.2)`, textAlign: 'center' }}>
            <Row className="align-items-center">
              <Col md={7} className="text-md-start">
                <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: colors.gold2, marginBottom: '8px' }}>Add-On</div>
                <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: colors.white, margin: 0 }}>
                  Agent <span style={{ color: colors.gold2 }}>Website</span>
                </h3>
                <p style={{ color: colors.muted, fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                  Your own branded domain with IDX search. Public-facing, SEO-ready, AI-powered. Works with any tier.
                </p>
                <div className="d-flex gap-3 mt-3 flex-wrap" style={{ fontSize: '13px', color: colors.off }}>
                  {['Custom domain & SSL', 'Professional templates', 'Full white-label branding', 'IDX MLS search', 'Mobile responsive'].map((f, i) => (
                    <span key={i}><span style={{ color: colors.gold2 }}>✓</span> {f}</span>
                  ))}
                </div>
              </Col>
              <Col md={5} className="text-center mt-3 mt-md-0">
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, lineHeight: 1 }}>
                  +$99<span style={{ fontSize: '16px', color: colors.muted }}>/mo</span>
                </div>
                <p style={{ fontSize: '13px', color: colors.gold2, marginTop: '4px', marginBottom: '16px' }}>+ $500 one-time setup</p>
                <Button
                  size="lg"
                  style={{ background: 'transparent', border: `2px solid ${colors.gold}`, color: colors.gold, fontSize: '14px', padding: '10px 30px', fontWeight: 600 }}
                  onClick={() => window.location.href = 'mailto:support@kairosiq.ai?subject=Website Add-On Interest'}
                >
                  Contact Us
                </Button>
              </Col>
            </Row>
          </div>

          {/* AI Workshop */}
          <div style={{ background: `linear-gradient(135deg, ${colors.navy2} 0%, ${colors.navy3} 100%)`, borderRadius: '12px', padding: '32px', marginTop: '32px', border: `1px solid rgba(201,149,42,0.3)`, textAlign: 'center' }}>
            <Row className="align-items-center">
              <Col md={7} className="text-md-start">
                <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: colors.gold2, marginBottom: '8px' }}>Hands-On Training</div>
                <h3 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: colors.white, margin: 0 }}>
                  AI For Real Estate <span style={{ color: colors.gold2 }}>Workshop</span>
                </h3>
                <p style={{ color: colors.muted, fontSize: '14px', marginTop: '8px', marginBottom: 0 }}>
                  Learn how to leverage AI in your real estate business. Hands-on training with live demos, practical strategies, and actionable takeaways. No subscription required.
                </p>
                <div className="d-flex gap-3 mt-3 flex-wrap" style={{ fontSize: '13px', color: colors.off }}>
                  {['Live hands-on demos', 'AI search strategies', 'CMA automation walkthrough', 'Lead generation with AI', 'Q&A with our team'].map((f, i) => (
                    <span key={i}><span style={{ color: colors.gold2 }}>✓</span> {f}</span>
                  ))}
                </div>
              </Col>
              <Col md={5} className="text-center mt-3 mt-md-0">
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, lineHeight: 1 }}>
                  $150<span style={{ fontSize: '16px', color: colors.muted }}> per seat</span>
                </div>
                <p style={{ fontSize: '13px', color: colors.gold2, marginTop: '4px', marginBottom: '16px' }}>No subscription required</p>
                <Button
                  size="lg"
                  style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', color: colors.navy, fontSize: '14px', padding: '10px 30px', fontWeight: 700 }}
                  onClick={() => window.location.href = '/events'}
                >
                  View Upcoming Workshops
                </Button>
              </Col>
            </Row>
          </div>

          <p style={{ textAlign: 'center', color: colors.muted, marginTop: '24px', fontSize: '14px' }}>
            No free trial. MLS access requires broker authorization (3-5 day approval). No contract — cancel anytime.
          </p>
        </Container>
      </section>

      {/* Feature Grid */}
      <section style={{ padding: '50px 0', borderTop: `1px solid rgba(201,149,42,0.15)` }}>
        <Container>
          <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '36px', color: colors.gold, letterSpacing: '2px', textAlign: 'center', marginBottom: '32px' }}>
            Why Agents Choose AI Search Portal
          </h2>
          <Row className="g-4">
            {[
              { icon: faChartLine, title: 'AI Valuation', desc: 'Trained on 1.6M+ closed sales with 197 zip-specific models. 90% prediction accuracy beats national tools.' },
              { icon: faRobot, title: 'Smart Buyer Search', desc: 'Buyers describe their dream home in natural language. Our AI finds matches from live MLS data — no filters needed.' },
              { icon: faBuilding, title: 'Your Brand First', desc: 'Every page shows your logo, photo, and brokerage. Clients see YOUR brand, not ours.' },
              { icon: faShieldAlt, title: 'Compliance Safe', desc: 'AI never gives pricing advice to sellers. Always defers to you. Fair Housing compliant. No AI disclosure to consumers.' },
              { icon: faUsers, title: 'Shareable Reports', desc: 'Generate CMA reports and share via branded links. Sellers chat with AI about their valuation — under your brand.' },
              { icon: faHome, title: 'Interactive CMA', desc: 'Review comps with MLS photos, add notes, adjust for condition (Fannie Mae C1-C6), and chat with AI about pricing strategy.' }
            ].map((feature, i) => (
              <Col md={4} key={i}>
                <div style={{ background: colors.navy2, borderRadius: '10px', padding: '24px', height: '100%', borderLeft: `3px solid ${colors.gold}` }}>
                  <FontAwesomeIcon icon={feature.icon} size="lg" style={{ color: colors.gold2, marginBottom: '12px' }} />
                  <h5 style={{ fontSize: '18px', fontWeight: 700, color: colors.white, marginBottom: '8px' }}>{feature.title}</h5>
                  <p style={{ fontSize: '14px', color: colors.muted, marginBottom: 0, lineHeight: 1.6 }}>{feature.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section style={{ background: `linear-gradient(90deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderTop: `3px solid ${colors.gold}`, padding: '30px 0' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: colors.gold2, letterSpacing: '1px' }}>Ready to deploy intelligence?</div>
              <div style={{ fontSize: '14px', color: colors.muted, marginTop: '4px' }}>Join agents who are already using AI to win more listings and serve buyers better.</div>
            </div>
            <Button
              size="lg"
              style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700, fontSize: '15px', letterSpacing: '0.5px', padding: '14px 28px' }}
              onClick={() => handleSignup('agent')}
            >
              Start Today — $70/month <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', padding: '30px 0 20px' }}>
        <Container>
          <Row className="align-items-center">
            <Col md={4}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px' }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
              <p style={{ fontSize: '11px', color: colors.muted, marginTop: '4px', marginBottom: 0 }}>
                AI-powered tools for real estate professionals. KairosIQ AI, Inc. · Chandler, Arizona
              </p>
            </Col>
            <Col md={4} className="text-center">
              <img
                src={require('../../assets/img/logos/equal-housing-opportunity-logo-1200w.jpg')}
                alt="Equal Housing Opportunity"
                style={{ height: '35px', objectFit: 'contain' }}
              />
              <p style={{ fontSize: '10px', color: colors.muted, marginTop: '4px', marginBottom: 0 }}>Equal Housing Opportunity</p>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-flex justify-content-end gap-3" style={{ fontSize: '12px' }}>
                <a href="/privacy-policy" style={{ color: colors.muted, textDecoration: 'none' }}>Privacy Policy</a>
                <a href="/terms-of-service" style={{ color: colors.muted, textDecoration: 'none' }}>Terms</a>
                <a href="/fair-housing" style={{ color: colors.muted, textDecoration: 'none' }}>Fair Housing</a>
              </div>
              <p style={{ fontSize: '10px', color: colors.muted, marginTop: '4px', marginBottom: 0 }}>
                © 2026 AISearchPortal · KairosIQ AI, Inc.
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default PricingPage;
